import { useRef, useCallback } from "react";
import socket from "../utils/socket";
import axios from "axios";
import { API_SERVER } from "../utils/config";

// Ensure this points to your Node.js backend
const API_BASE = API_SERVER; 

export default function useWebRTC({ selfId, setLocalStream, setRemoteStream }) {
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const iceCandidatesQueue = useRef([]);

  /**
   * 🛡️ Helper: Process any ICE candidates that arrived before 
   * the remote description was set.
   */
  const processIceQueue = async () => {
    if (!peerRef.current || !peerRef.current.remoteDescription) return;
    
    while (iceCandidatesQueue.current.length > 0) {
      const candidate = iceCandidatesQueue.current.shift();
      try {
        await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error("❌ Error adding queued ICE candidate:", err);
      }
    }
  };

  /**
   * 🌐 API Call: Fetches Cloudflare TURN credentials from your backend.
   * Includes a 3-second timeout and a fallback to Google STUN.
   */
  const getIceServers = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/webrtc/ice-servers`, { 
        timeout: 3000 
      });
      console.log("🚀 Cloudflare TURN credentials received");
      return response.data.iceServers;
    } catch (error) {
      console.warn("⚠️ Cloudflare TURN Fetch Failed. Falling back to Google STUN.");
      return [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" }
      ];
    }
  };

  const startWebRTC = useCallback(async (peerId, type, role, remoteOffer = null) => {
    if (!peerId) return console.error("❌ No Peer ID provided");
    
    // 🛑 Prevent duplicate PeerConnections
    if (peerRef.current) {
      console.warn("⚠️ PeerConnection already exists. Skipping creation.");
      return;
    }

    try {
      // 1️⃣ Get Media Permissions
      const constraints = {
        audio: true,
        video: type === "video"
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      setLocalStream(stream);

      // 2️⃣ Fetch Dynamic ICE Servers (Cloudflare 1TB)
      const dynamicIceServers = await getIceServers();

      // 3️⃣ Create Peer Connection
      const pc = new RTCPeerConnection({
        iceServers: dynamicIceServers,
        iceTransportPolicy: 'all', 
      });
      peerRef.current = pc;

      // 4️⃣ Handle Local Tracks
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      // 5️⃣ Handle Remote Tracks
      pc.ontrack = (event) => {
        console.log("📺 Remote Stream Received");
        if (event.streams && event.streams[0]) {
          remoteStreamRef.current = event.streams[0];
          setRemoteStream(new MediaStream(event.streams[0].getTracks()));
        }
      };

      // 6️⃣ Handle ICE Candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("call:ice-candidate", { 
            to: peerId, 
            from: selfId, 
            candidate: event.candidate 
          });
        }
      };

      // 7️⃣ Signaling Flow
      if (role === "caller") {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit("call:offer", { to: peerId, from: selfId, offer, type });
      } 
      else if (role === "callee" && remoteOffer) {
        await pc.setRemoteDescription(new RTCSessionDescription(remoteOffer));
        await processIceQueue(); // Add any candidates that arrived early
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("call:answer", { to: peerId, from: selfId, answer });
      }

    } catch (err) {
      console.error("❌ WebRTC Initialization Error:", err);
      alert("Call setup failed. Please check camera/mic permissions.");
    }
  }, [selfId, setLocalStream, setRemoteStream]);

  const applyAnswer = useCallback(async ({ answer }) => {
    if (peerRef.current && peerRef.current.signalingState !== "stable") {
      try {
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
        await processIceQueue();
      } catch (err) {
        console.error("❌ Error applying answer:", err);
      }
    }
  }, []);

  const applyRemoteIce = useCallback(async ({ candidate }) => {
    if (peerRef.current && peerRef.current.remoteDescription) {
      try {
        await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error("❌ Error applying remote ICE:", err);
      }
    } else {
      // Queue candidate if remote description isn't ready
      iceCandidatesQueue.current.push(candidate);
    }
  }, []);

  const endWebRTC = useCallback(({ notifyPeer, toId } = {}) => {
    if (notifyPeer && toId) {
      socket.emit("call:end", { to: toId, from: selfId });
    }

    if (peerRef.current) {
      peerRef.current.getSenders().forEach(sender => peerRef.current.removeTrack(sender));
      peerRef.current.close();
      peerRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    setLocalStream(null);
    setRemoteStream(null);
    remoteStreamRef.current = null;
    iceCandidatesQueue.current = [];
  }, [selfId, setLocalStream, setRemoteStream]);

  return { startWebRTC, endWebRTC, applyAnswer, applyRemoteIce };
}